import * as React from 'react'
import StageHeader from '../../shared/StageHeader'
import FormLayout from '../../shared/FormLayout'
import { useContractForm } from '@/modules/drivers/context/ContractFormContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import CheckboxWithLabelField from '../inputs/CheckboxWithLabelField'
import LabelHeader from '../components/LabelHeader'
import NumberFieldInnerTable from '../inputs/NumberFieldInnerTable'

import RateReduceCustom from '../components/shared/RateReduceCustom'

const ContractLumpsCosts = () => {
  const { t } = useLocalize()
  const { setData, data } = useContractForm()

  const hasCollectiveRate = data?.lumpSumAllRates != null
  const splitFields = [
    'lumpSumDutyFiftyPercentRate', 'lumpSumDutyOneHundredRate', 'lumpSumDutyRate',
    'lumpSumNightRate', 'lumpSumNightHourRate', 'lumpSumOvertimeFiftyPercentRate',
    'lumpSumOvertimeOneHundredPercentRate', 'lumpSumOvertimeRate',
  ] as const
  const hasSplitRate = splitFields.some((key) => data?.[key] != null)

  const lumps = [
    {
      key: 'lumpSumOvertimeRate',
      label: t('contract.overtime'),
      mainCheckboxProperty: 'reduceOvertimeRates',
      sickLeaveProperty: 'reduceOvertimeRatesForSickLeave',
      paidLeaveProperty: 'reduceOvertimeRatesForLeave',
      unpaidLeaveProperty: 'reduceOvertimeRatesForNotPaidLeave',
    },
    {
      key: 'lumpSumNightRate',
      label: t('contract.night-shifts'),
      mainCheckboxProperty: 'reduceNightRates',
      sickLeaveProperty: 'reduceNightRatesForSickLeave',
      paidLeaveProperty: 'reduceNightRatesForLeave',
      unpaidLeaveProperty: 'reduceNightRatesForNotPaidLeave',
    },
    {
      key: 'lumpSumDutyRate',
      label: t('contract.duty-shifts'),
      mainCheckboxProperty: 'reduceDutyRates',
      sickLeaveProperty: 'reduceDutyRatesForSickLeave',
      paidLeaveProperty: 'reduceDutyRatesForLeave',
      unpaidLeaveProperty: 'reduceDutyRatesForNotPaidLeave',
    },
  ] as const

  return (
    <div className="tw-flex tw-min-h-[500px]  tw-justify-between">
      <StageHeader title={t('contract.lump-costs')} />
      <FormLayout>
        <NumberFieldInnerTable
          id="contract-collective-lump-sum"
          inputSize="big"
          fullWidth
          label={t('contract.collective-lump-sum')}
          innerLabel="PLN"
          placeholder="0,00"
          value={data?.lumpSumAllRates?.toString() ?? ''}
          disabled={!hasCollectiveRate && hasSplitRate}
          helper={t('contract.collective-lump-sum-help')}
          onChange={(e) => setData({
            ...data,
            lumpSumAllRates: e.target.value === '' ? undefined : Number(e.target.value),
          })}
        />
        <CheckboxWithLabelField
          id="contract-reduce-collective-lump-sum"
          label={t('contract.reduce-collective-lump-sum')}
          field="reduceAllRates"
          data={data}
          setData={setData}
        />
        <LabelHeader label={t('contract.split-contributions')} />
        {lumps.map(
          ({
            key,
            label,
            mainCheckboxProperty,
            sickLeaveProperty,
            paidLeaveProperty,
            unpaidLeaveProperty,
          }) => {
            const value = data?.[key]
            return (
              <div key={key}>
                <NumberFieldInnerTable
                  inputSize="big"
                  fullWidth
                  label={label}
                  innerLabel="PLN"
                  placeholder="0,00"
                  value={value?.toString()}
                  disabled={hasCollectiveRate && value == null}
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    setData({ ...data, [key]: val > 0 ? val : undefined })
                  }}
                />
                <RateReduceCustom
                  data={data}
                  setData={setData}
                  reduceType="rate"
                  background
                  mainCheckboxProperty={
                    mainCheckboxProperty as keyof typeof data
                  }
                  sickLeaveProperty={sickLeaveProperty as keyof typeof data}
                  paidLeaveProperty={paidLeaveProperty as keyof typeof data}
                  unpaidLeaveProperty={unpaidLeaveProperty as keyof typeof data}
                  sickLeaveLabel={t('contract.sick-leave-1-30')}
                  paidLeaveLabel={t('contract.paid-leave-by-dimension')}
                  unpaidLeaveLabel={t('contract.unpaid-leave-by-dimension')}
                />
              </div>
            )
          },
        )}
      </FormLayout>
    </div>
  )
}

export default ContractLumpsCosts
